import AppIcon from "../components/AppIcon";
import { useState, useEffect } from "react";
import { IMAGES } from "../constants/images";
import Sidebar from "../components/Sidebar";
import { LuSearch as CiSearch } from "react-icons/lu";
import { ref, onValue, off, remove } from "firebase/database";
import { database } from "../constants/firebase";
import useMoveTo from "../constants/navigation";
import { ROUTES } from "../App";
import { useUser } from "../contexts/UserContext"; // ✅ get currentUser
import MyCommunityItem from "../components/MyCommunityItem";
import MobileHeader from "../components/MobileHeader";
import DeleteModal from "../components/DeleteModal";

const MyCommunity = () => {
  const navigate = useMoveTo();
  const { currentUser } = useUser(); // ✅ logged-in user
  const [searchQuery, setSearchQuery] = useState("");
  const [communities, setCommunities] = useState([]);
  const [filteredCommunities, setFilteredCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (!currentUser) return;

    const communitiesRef = ref(database, "Communities");
    onValue(communitiesRef, (snapshot) => {
      const data = snapshot.val() || {};

      // ✅ filter only owned communities
      const ownedCommunities = Object.entries(data)
        .map(([id, community]) => ({ id, ...community }))
        .filter((c) => c.hostId === currentUser.uid);

      setCommunities(ownedCommunities);
      setFilteredCommunities(ownedCommunities);
      setLoading(false);
    });

    return () => off(communitiesRef);
  }, [currentUser]);

  // 🔍 filter by search
  useEffect(() => {
    if (searchQuery) {
      const filtered = communities.filter(
        (c) =>
          c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          c.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          c.hostName?.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      setFilteredCommunities(filtered);
    } else {
      setFilteredCommunities(communities);
    }
  }, [searchQuery, communities]);

  // ✅ Delete handler
  const handleDelete = async () => {
    if (!selectedId) return;
    try {
      setIsDeleting(true);
      await remove(ref(database, `Communities/${selectedId}`));
      setCommunities((prev) => prev.filter((c) => c.id !== selectedId));
      setFilteredCommunities((prev) => prev.filter((c) => c.id !== selectedId));
      window.showToast("Community deleted successfully", "success");
    } catch (err) {
      console.error("Delete failed:", err);
      window.showToast("Failed to delete community", "error");
    } finally {
      setIsDeleting(false);
      setIsDeleteOpen(false);
      setSelectedId(null);
    }
  };

  // 🟢 Simple shimmer skeleton loader component
  const ShimmerLoader = () => (
    <div className="flex flex-col space-y-4 mt-8">
      {[...Array(4)].map((_, i) => (
        <div
          key={i}
          className="animate-pulse flex items-center justify-between p-4 border-t border-gray-200 dark:border-[#D9D9D9]/20"
        >
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-gray-300 dark:bg-gray-700 rounded-lg"></div>
            <div>
              <div className="w-40 h-4 bg-gray-300 dark:bg-gray-700 rounded mb-2"></div>
            </div>
          </div>
          <div className="w-16 h-6 bg-gray-300 dark:bg-gray-700 rounded"></div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="h-screen bg-white text-black dark:bg-[#23272c] dark:text-white overflow-y-auto">
      <MobileHeader title="My Communities" isBackBtn={true} />

      <div className="flex">
        <Sidebar />
        <main className="flex-1 h-screen bg-white dark:bg-[#23272c] px-6 pt-4 md:pt-8 pb-4 flex flex-col">
          <div className="flex items-center justify-between px-2">
            <button
              onClick={() => navigate(-1)}
              className="flex-shrink-0 md:block hidden"
            >
              <AppIcon
                name="arrowBack"
                className="w-4 h-4 md:w-5 md:h-5 grayscale brightness-0 dark:invert"
                alt="Back"
              />
            </button>
            <h2 className="text-xl md:text-3xl font-semibold hidden md:block">
              My Communities
            </h2>
            <div className="w-5 md:hidden block" />
            <button
              onClick={() => navigate(ROUTES.NEW_COMMUNITY)}
              className="px-4 py-2 text-sm md:text-[1rem] rounded-xl font-semibold border border-[#327691] bg-[#327691] text-white"
            >
              Create a Community
            </button>
          </div>

          <div className="flex items-center space-x-8 mt-8 mb-2">
            <div className="relative flex-1">
              <CiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-xl" />
              <input
                type="text"
                placeholder="Search communities"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-xl pl-10 pr-4 py-2 bg-[#F5F9FE] dark:bg-white text-black border border-gray-200"
              />
            </div>
          </div>

          <div className="mt-3 flex-1 overflow-y-auto pr-1 pb-12">
            {loading ? (
              <ShimmerLoader />
            ) : (
              <MyCommunityItem
                communities={filteredCommunities}
                navigate={navigate}
                setSelectedId={setSelectedId}
                setIsDeleteOpen={setIsDeleteOpen}
              />
            )}
          </div>
          <DeleteModal
            isOpen={isDeleteOpen}
            onClose={() => setIsDeleteOpen(false)}
            onConfirm={handleDelete}
            isDeleting={isDeleting}
          />
        </main>
      </div>
    </div>
  );
};

export default MyCommunity;
